import { useEmpresasConciliaveis } from "../../lib/conciliacao/hooks";
import { ErroHttp } from "../../lib/http/erros";

/*
 * Seletor de empresa da conciliação (FE-3 CP-B — leitura).
 *
 * Espelha o padrão do `projecao/SeletorEscopo`, mas é uma peça PRÓPRIA da
 * conciliação (acoplá-lo ao seletor de projeção seria errado: conciliação é
 * SEMPRE por empresa — não há escopo de grupo, nem opção "consolidado").
 *
 * Consome `useEmpresasConciliaveis()` — a lista JÁ vem filtrada pelo escopo do
 * usuário no backend (GET /api/conciliacao/empresas). A UI não filtra empresa do
 * lado do cliente: apresenta apenas o que o servidor autorizou.
 *
 * Rótulo de cada empresa: `fantasia ?? razao_social` (nem toda empresa cadastra
 * nome fantasia).
 *
 * ESCOPO VAZIO: quando a lista vem `[]` (200 vazio, usuário sem empresa
 * conciliável), renderiza um estado CLARO em vez de um `<select>` vazio confuso
 * — espelha o vazio do `SeletorEscopo`.
 */

interface SeletorEmpresaConciliacaoProps {
  /** Id da empresa atualmente selecionada, ou `null` enquanto nada foi escolhido. */
  selecionada: string | null;
  /** Notifica a página da empresa escolhida. */
  aoSelecionar: (empresaId: string) => void;
}

export function SeletorEmpresaConciliacao({
  selecionada,
  aoSelecionar,
}: SeletorEmpresaConciliacaoProps): JSX.Element {
  const consulta = useEmpresasConciliaveis();

  if (consulta.isPending) {
    return (
      <p className="font-corpo text-base text-off-black">
        Carregando empresas…
      </p>
    );
  }

  if (consulta.isError) {
    const mensagem =
      consulta.error instanceof ErroHttp
        ? consulta.error.message
        : "Não foi possível carregar as empresas.";
    return (
      <p role="alert" className="font-corpo text-base text-flame">
        {mensagem}
      </p>
    );
  }

  const empresas = consulta.data;

  // ESCOPO VAZIO: nada a conciliar — estado claro, não um seletor vazio.
  if (empresas.length === 0) {
    return (
      <p className="font-corpo text-base text-off-black">
        Você não tem empresas para conciliar.
      </p>
    );
  }

  function aoMudar(evento: React.ChangeEvent<HTMLSelectElement>): void {
    const valor = evento.target.value;
    if (valor === "") {
      return;
    }
    aoSelecionar(valor);
  }

  return (
    <label className="flex flex-col gap-1 font-corpo text-base text-off-black">
      <span className="font-subtitulo">Empresa</span>
      <select
        value={selecionada ?? ""}
        onChange={aoMudar}
        className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
      >
        <option value="" disabled>
          Selecione…
        </option>
        {empresas.map((empresa) => (
          <option key={empresa.id} value={empresa.id}>
            {empresa.fantasia ?? empresa.razao_social}
          </option>
        ))}
      </select>
    </label>
  );
}
